import React, { useCallback, useMemo, useState } from "react";
import {
    flexRender,
    getCoreRowModel,
    useReactTable,
    ColumnDef,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    ColumnFiltersState,
    FilterFn,
    getFilteredRowModel,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFacetedMinMaxValues,
    Row,
    PaginationState,
    TableState,
    TableOptions,
} from "@tanstack/react-table";
import { rankItem } from "@tanstack/match-sorter-utils";
import { Table as BootstrapTable, Form, InputGroup, Spinner } from "react-bootstrap";
import { debounce } from "lodash-es";
import { Icon, fas } from "../icons";
import { ColumnFilter, DataTablePagination, PageSizeDropdown } from ".";
import { PagedResponse, ParamsApi, SortParams } from "../../models";
import { AutoCol } from "..";

export interface DataTableProps {
    columns: ColumnDef<any>[];
    data?: any[];
    pagedData?: PagedResponse<any>;
    onRowClick?: (row: Row<any>) => void;
    paginated?: boolean;
    paramsApi?: ParamsApi;
    pageSize?: number;
    className?: string;
    showGlobalFilter?: boolean;
    filterColumns?: boolean;
    disableSort?: boolean;
    noStripes?: boolean;
    noHover?: boolean;
    globalSearchValue?: string;
    getRowClassName?: (row: Row<any>) => string;
    loading?: boolean;
    hidePageSize?: boolean;
    showTotalCount?: boolean;
}

export const DataTable = (props: DataTableProps) => {
    const {
        columns,
        data,
        pagedData,
        onRowClick,
        paginated,
        paramsApi,
        className,
        showGlobalFilter,
        filterColumns,
        disableSort,
        noStripes,
        noHover,
        globalSearchValue,
        getRowClassName,
        loading,
        hidePageSize,
        showTotalCount,
    } = props;

    const { setPageParams, setSortParams, updatePageParams, pageParams, sortParams } = paramsApi ?? {};
    const totalCount = pagedData ? pagedData.totalCount : (data?.length ?? 0);

    const initialSortingState = useMemo<SortingState>(() => {
        if (sortParams) {
            return [
                {
                    id: sortParams.orderBy,
                    desc: sortParams.orderDesc === "true",
                },
            ];
        }
        return [];
    }, [sortParams]);

    const initialPaginationState = useMemo<PaginationState>(() => {
        if (pageParams) {
            return {
                pageIndex: pageParams.page ? pageParams.page - 1 : 0,
                pageSize: pageParams.pageSize ?? 10,
            };
        }
        return {
            pageIndex: 0,
            pageSize: props.pageSize ?? -1,
        };
    }, [pageParams, props.pageSize]);

    const [sorting, setSorting] = useState<SortingState>(initialSortingState);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [globalFilter, setGlobalFilter] = useState("");
    const [pagination, setPagination] = useState<PaginationState>(initialPaginationState);

    const tableData = useMemo<any[]>(() => {
        if (pagedData) {
            return pagedData.items;
        }
        return data ?? [];
    }, [data, pagedData]);

    const isPaginated = paginated ?? !!pagedData;

    const handlePaginationChange = useCallback(
        (fn: any) => {
            const updatedPagination = fn({ ...pagination });
            setPagination(updatedPagination);
            if (setPageParams) {
                setPageParams({
                    page: updatedPagination.pageIndex + 1,
                    pageSize: updatedPagination.pageSize,
                });
            }
        },
        [pagination, setPageParams]
    );

    const handleSortChange = useCallback(
        (fn: any) => {
            const newSortingState = fn();
            if (!newSortingState.length) {
                setSorting([]);
                if (setSortParams) {
                    setSortParams({ orderBy: "", orderDesc: "" });
                }
            } else {
                setSorting(newSortingState);
                if (setSortParams) {
                    const updatedSortParams: SortParams = {
                        orderBy: newSortingState[0].id,
                        orderDesc: newSortingState[0].desc ? "true" : "false",
                    };
                    setSortParams(updatedSortParams);
                }
            }
        },
        [setSortParams]
    );

    const debounceGlobalSearch = debounce((value: string) => setGlobalFilter(value), 500);

    const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
        const itemRank = rankItem(row.getValue(columnId), value);
        addMeta({
            itemRank,
        });
        return itemRank.passed;
    };

    const tableState = useMemo<Partial<TableState>>(() => {
        if (pagedData)
            return {
                sorting,
                columnFilters,
                globalFilter: globalSearchValue ?? globalFilter,
                pagination: pagination,
            };
        return {
            sorting,
            columnFilters,
            globalFilter: globalSearchValue ?? globalFilter,
        };
    }, [pagedData, sorting, columnFilters, globalSearchValue, globalFilter, pagination]);

    const tableOptions = useMemo<TableOptions<any>>(() => {
        const builder: TableOptions<any> = {
            data: tableData,
            columns,
            manualPagination: !!pagedData,
            manualSorting: !!pagedData,
            enableSorting: !disableSort,
            enableMultiSort: false,
            onSortingChange: handleSortChange,
            enableSortingRemoval: false,
            filterFns: {
                fuzzy: fuzzyFilter,
            },
            state: tableState,
            globalFilterFn: fuzzyFilter,
            getCoreRowModel: getCoreRowModel(),
            getPaginationRowModel: getPaginationRowModel(),
            getSortedRowModel: getSortedRowModel(),
            onColumnFiltersChange: setColumnFilters,
            onGlobalFilterChange: setGlobalFilter,
            getFilteredRowModel: getFilteredRowModel(),
            getFacetedRowModel: getFacetedRowModel(),
            getFacetedUniqueValues: getFacetedUniqueValues(),
            getFacetedMinMaxValues: getFacetedMinMaxValues(),
        };
        if (pagedData) {
            builder.pageCount = pagedData?.totalPages ?? -1;
            builder.onPaginationChange = handlePaginationChange;
        }
        return builder;
    }, [columns, disableSort, handleSortChange, handlePaginationChange, pagedData, tableData, tableState]);

    const table = useReactTable(tableOptions);
    const { getHeaderGroups, getRowModel, getState, setPageSize } = table;
    const pageSize = getState().pagination.pageSize;
    const showPageSizeDropdown = !!pagedData && !hidePageSize && !!pageParams && !!updatePageParams;

    return (
        <>
            {showGlobalFilter && (
                <div className="d-flex justify-content-end">
                    <Form.Group className="mb-1">
                        <InputGroup>
                            <Form.Control
                                type="text"
                                onChange={(e) => debounceGlobalSearch(e.target.value)}
                                placeholder="Search all columns..."
                                style={{ maxWidth: "200px" }}
                            />
                            <InputGroup.Text className="bg-primary text-white">
                                <Icon icon={fas.faSearch} />
                            </InputGroup.Text>
                        </InputGroup>
                    </Form.Group>
                </div>
            )}

            {isPaginated && !pagedData && (
                <div className="d-flex justify-content-end mb-1">
                    <AutoCol>
                        <Form.Select value={pageSize} onChange={(e) => setPageSize(+e.target.value)}>
                            {[10, 25, 50, 100].map((size) => (
                                <option key={size} value={size}>
                                    Show {size}
                                </option>
                            ))}
                        </Form.Select>
                    </AutoCol>
                </div>
            )}

            {(showTotalCount || showPageSizeDropdown) && (
                <div className="d-flex justify-content-between align-items-end">
                    <div className="d-flex align-items-end">{showTotalCount && <>Total: {totalCount}</>}</div>
                    {showPageSizeDropdown && (
                        <PageSizeDropdown pageParams={pageParams} updatePageParams={updatePageParams} />
                    )}
                </div>
            )}

            <BootstrapTable striped={!noStripes} hover={!noHover} className={className ?? ""}>
                <thead>
                    {getHeaderGroups().map((headerGroup, i) => (
                        <React.Fragment key={`headerGroup-${i}`}>
                            <tr>
                                {headerGroup.headers.map((header, j) => (
                                    <th key={`headerGroup-${i}-header-${j}`} colSpan={header.colSpan}>
                                        {header.isPlaceholder ? null : (
                                            <>
                                                <div
                                                    {...{
                                                        className:
                                                            !disableSort && header.column.getCanSort()
                                                                ? "clickable"
                                                                : "",
                                                        onClick: disableSort
                                                            ? () => {}
                                                            : header.column.getToggleSortingHandler(),
                                                    }}
                                                >
                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                    {{
                                                        asc: <Icon icon={fas.faSortUp} className="text-info ms-2" />,
                                                        desc: <Icon icon={fas.faSortDown} className="text-info ms-2" />,
                                                    }[header.column.getIsSorted() as string] ?? null}
                                                </div>
                                                {filterColumns && header.column.getCanFilter() ? (
                                                    <div>
                                                        <ColumnFilter column={header.column} table={table} />
                                                    </div>
                                                ) : null}
                                            </>
                                        )}
                                    </th>
                                ))}
                            </tr>
                        </React.Fragment>
                    ))}
                </thead>
                <tbody>
                    {!loading ? (
                        getRowModel().rows.map((row, i) => (
                            <tr
                                key={`row-${i}`}
                                onClick={() => onRowClick?.(row)}
                                className={`${onRowClick ? "clickable" : ""} ${getRowClassName ? getRowClassName(row) : ""}`}
                            >
                                {row.getVisibleCells().map((cell, j) => (
                                    <td key={`row-${i}-cell-${j}`}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={columns.length} className="text-center">
                                <Spinner animation="grow" variant="primary" />
                            </td>
                        </tr>
                    )}
                </tbody>
            </BootstrapTable>
            {isPaginated && <DataTablePagination table={table} className="mb-4" />}
        </>
    );
};
